class MpsVideoPlayer extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('.mps-video__el');
    this.toggleBtn = this.querySelector('[data-video-toggle]');
    this.muteBtn = this.querySelector('[data-video-mute]');
    this.autohideDelay = Number(this.dataset.autohide) || 2000;
    this.hideTimer = null;
    const { labelPlay, labelPause, labelMute, labelUnmute } = this.dataset;
    this.labels = {
      play: labelPlay || 'Play video',
      pause: labelPause || 'Pause video',
      mute: labelMute || 'Mute video',
      unmute: labelUnmute || 'Unmute video',
    };

    if (!this.video) return;

    this.handleToggle = this.handleToggle.bind(this);
    this.handleMute = this.handleMute.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerLeave = this.hideControls.bind(this);
    this.handleReveal = this.handleReveal.bind(this);
    this.syncPlayState = this.syncPlayState.bind(this);

    this.toggleBtn?.addEventListener('click', this.handleToggle);
    this.muteBtn?.addEventListener('click', this.handleMute);
    this.addEventListener('pointermove', this.handlePointerMove);
    this.addEventListener('pointerleave', this.handlePointerLeave);
    this.addEventListener('click', this.handleReveal);
    this.video.addEventListener('play', this.syncPlayState);
    this.video.addEventListener('pause', this.syncPlayState);

    this.syncPlayState();
    this.syncMuteState();
    this.observeSources();
  }

  // The gallery renders this player up to 3 times (mobile slider, desktop grid, lightbox). Each copy attaches
  // its source only once on screen, so hidden copies never download the video.
  observeSources() {
    this.heldSources = [...this.video.querySelectorAll('source[data-src]')];
    if (!this.heldSources.length) return;

    this.sourceObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.loadSources();
    }, { rootMargin: '200px' });
    this.sourceObserver.observe(this);
  }

  loadSources() {
    this.sourceObserver?.disconnect();
    for (const source of this.heldSources) {
      source.src = source.dataset.src;
      source.removeAttribute('data-src');
    }
    this.video.load();
  }

  disconnectedCallback() {
    clearTimeout(this.hideTimer);
    this.sourceObserver?.disconnect();
    this.toggleBtn?.removeEventListener('click', this.handleToggle);
    this.muteBtn?.removeEventListener('click', this.handleMute);
    this.removeEventListener('pointermove', this.handlePointerMove);
    this.removeEventListener('pointerleave', this.handlePointerLeave);
    this.removeEventListener('click', this.handleReveal);
    this.video?.removeEventListener('play', this.syncPlayState);
    this.video?.removeEventListener('pause', this.syncPlayState);
  }

  handleToggle() {
    if (this.video.paused) {
      this.video.play();
    } else {
      this.video.pause();
    }
    this.showControls();
  }

  handleMute() {
    this.video.muted = !this.video.muted;
    this.syncMuteState();
    this.showControls();
  }

  handlePointerMove() {
    this.showControls();
  }

  handleReveal(event) {
    // Buttons manage their own state; only bare-frame taps reveal the controls
    if (event.target.closest('[data-video-toggle], [data-video-mute]')) return;
    this.showControls();
  }

  syncPlayState() {
    const isPlaying = !this.video.paused;
    this.classList.toggle('is-playing', isPlaying);
    this.toggleBtn?.setAttribute('aria-label', isPlaying ? this.labels.pause : this.labels.play);
    // Keep controls visible while paused; auto-hide while playing
    if (isPlaying) {
      this.scheduleHide();
    } else {
      this.showControls(false);
    }
  }

  syncMuteState() {
    const { muted } = this.video;
    this.classList.toggle('is-unmuted', !muted);
    this.muteBtn?.setAttribute('aria-pressed', String(muted));
    this.muteBtn?.setAttribute('aria-label', muted ? this.labels.unmute : this.labels.mute);
  }

  showControls(autoHide = true) {
    this.classList.add('is-controls-visible');
    clearTimeout(this.hideTimer);
    if (autoHide && !this.video.paused) {
      this.scheduleHide();
    }
  }

  scheduleHide() {
    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => this.hideControls(), this.autohideDelay);
  }

  hideControls() {
    if (this.video.paused) return;
    this.classList.remove('is-controls-visible');
  }
}

if (!customElements.get('mps-video-player')) {
  customElements.define('mps-video-player', MpsVideoPlayer);
}
